"use client"

// Platform-aware modifier-key labels for the marketing site.
//
// The provider lives in app/layout.tsx so every page (static OR dynamic) has
// access without per-page wiring. Detection runs CLIENT-side only — the root
// layout intentionally avoids `headers()` to keep static pages prerenderable
// (see comment in app/layout.tsx). The trade-off: Windows visitors see "⌘"
// for one render before the effect updates context to "Ctrl", which is fine
// for a single-character modifier label and avoids forcing every page dynamic
// just to win zero-flash.
//
// Optional `initialOs` prop: dynamic pages that already read headers (the
// /pricing-currency tree) can pass the server-detected OS in to skip the
// flash, but it's not required. Most pages mount the provider with no prop
// and rely on the client-side detection.

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react"
import {
  formatCompactHotkey,
  formatSpacedHotkey,
  getCommandKeySymbol,
  parseOsFromUserAgent,
  type OsPlatform,
} from "@/lib/config/platform"

interface PlatformContextValue {
  os: OsPlatform
  /** "⌘" on Mac, "Ctrl" everywhere else. */
  cmd: string
  /** "⌘ + I" / "Ctrl + I" — the spaced marketing style. */
  spaced: (key: string) => string
  /** "⌘G" / "Ctrl+G" — the compact keyboard-hint-pill style. */
  compact: (key: string) => string
}

// Default to "mac" for the initial render. macOS is the canonical platform we
// market against, so Mac users (the larger marketing audience) see the
// correct label without flashing. Windows users get a one-render flash from
// "⌘" → "Ctrl" once the effect runs.
const PlatformContext = createContext<PlatformContextValue>({
  os: "mac",
  cmd: "⌘",
  spaced: (key) => `⌘ + ${key}`,
  compact: (key) => `⌘${key}`,
})

export function PlatformProvider({
  initialOs,
  children,
}: {
  /** Optional server-detected platform (from getOsFromHeaders). When provided,
   * the provider skips the post-mount detection — useful for pages that are
   * already dynamic and want zero-flash rendering. */
  initialOs?: OsPlatform
  children: ReactNode
}) {
  const [os, setOs] = useState<OsPlatform>(initialOs ?? "mac")

  useEffect(() => {
    if (initialOs) return
    if (typeof navigator === "undefined") return
    setOs(parseOsFromUserAgent(navigator.userAgent))
  }, [initialOs])

  const value = useMemo<PlatformContextValue>(
    () => ({
      os,
      cmd: getCommandKeySymbol(os),
      spaced: (key: string) => formatSpacedHotkey(os, key),
      compact: (key: string) => formatCompactHotkey(os, key),
    }),
    [os]
  )

  return <PlatformContext.Provider value={value}>{children}</PlatformContext.Provider>
}

export function usePlatform(): OsPlatform {
  return useContext(PlatformContext).os
}

/** Modifier-key label only ("⌘" or "Ctrl"). */
export function useCommandKeySymbol(): string {
  return useContext(PlatformContext).cmd
}

/**
 * Spaced hotkey formatter ("⌘ + I" / "Ctrl + I"). Use this for marketing copy
 * where the modifier stands on its own with a "+ Key" tail.
 */
export function useSpacedHotkey(): (key: string) => string {
  return useContext(PlatformContext).spaced
}

/**
 * Compact hotkey formatter ("⌘G" / "Ctrl+G"). Use this for keyboard-hint
 * pills inside desktop-app preview screenshots where the modifier sits flush
 * against the next key glyph.
 */
export function useCompactHotkey(): (key: string) => string {
  return useContext(PlatformContext).compact
}
